import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Select,
  Text,
  Popover,
  Alert,
  Checkbox,
  Paper,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconPaperclip, IconDeviceFloppy } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import TextEditor from "./textEditor";
import EmailPlaceholderLegend from "./emailPlaceholderLegend";
import EntityEmailStoredFiles from "./entityEmailStoredFiles";
import {
  AR_FORM_EMAIL_PLACEHOLDERS,
  AR_FORM_PASSWORD_PLACEHOLDERS,
  DEFAULT_AR_FORM_EMAIL_BODY,
  DEFAULT_AR_FORM_EMAIL_SUBJECT,
  DEFAULT_AR_FORM_PASSWORD_BODY,
  DEFAULT_AR_FORM_PASSWORD_SUBJECT,
} from "../data/emailPlaceholders";

const BUILTIN_TEMPLATE = "__builtin__";
const EMAIL_RE = /\S+@\S+\.\S+/;

function isValidEmailList(value, { required = false } = {}) {
  const parts = String(value || "")
    .split(/[,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (!parts.length) return !required;
  return parts.every((part) => EMAIL_RE.test(part));
}

function uniqueTemplates(docs) {
  const byName = new Map();
  docs.forEach((doc) => {
    const key = `${doc.type || ""}|${String(doc.name || "").trim().toLowerCase()}`;
    const prev = byName.get(key);
    if (!prev) {
      byName.set(key, doc);
      return;
    }
    const newer =
      new Date(doc.updatedAt || doc.createdAt || 0) >=
      new Date(prev.updatedAt || prev.createdAt || 0)
        ? doc
        : prev;
    byName.set(key, newer);
  });
  return Array.from(byName.values());
}

function formatSentAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

export default function SendArFormEmailModal({ opened, onClose, form: transactionForm }) {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [templates, setTemplates] = useState([]);
  const [passwordTemplates, setPasswordTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savePasswordOpen, setSavePasswordOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [passwordTemplateName, setPasswordTemplateName] = useState("");
  const [bodyKey, setBodyKey] = useState(0);
  const [passwordBodyKey, setPasswordBodyKey] = useState(0);
  const [activeField, setActiveField] = useState("body");
  const [filename, setFilename] = useState("");
  const [entityFiles, setEntityFiles] = useState([]);
  const [selectedEntityFileIds, setSelectedEntityFileIds] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [preview, setPreview] = useState(null);

  const transactionId = transactionForm?.values?._id;

  const form = useForm({
    initialValues: {
      templateId: BUILTIN_TEMPLATE,
      passwordTemplateId: BUILTIN_TEMPLATE,
      to: "",
      cc: "",
      bcc: "",
      subject: DEFAULT_AR_FORM_EMAIL_SUBJECT,
      body: DEFAULT_AR_FORM_EMAIL_BODY,
      encrypt: false,
      passwordSubject: DEFAULT_AR_FORM_PASSWORD_SUBJECT,
      passwordBody: DEFAULT_AR_FORM_PASSWORD_BODY,
    },
    validate: {
      to: (value) =>
        isValidEmailList(value, { required: true })
          ? null
          : t("Enter a valid email address"),
      cc: (value) =>
        isValidEmailList(value) ? null : t("Enter a valid CC email address"),
      bcc: (value) =>
        isValidEmailList(value) ? null : t("Enter a valid BCC email address"),
      subject: (value) =>
        !value || !String(value).trim() ? t("Subject is required") : null,
    },
  });

  const applyDocumentTemplate = useCallback((tpl) => {
    form.setFieldValue("templateId", tpl?._id || BUILTIN_TEMPLATE);
    form.setFieldValue("subject", tpl?.subject || DEFAULT_AR_FORM_EMAIL_SUBJECT);
    form.setFieldValue("body", tpl?.body || DEFAULT_AR_FORM_EMAIL_BODY);
    form.setFieldValue("cc", tpl?.cc || "");
    form.setFieldValue("bcc", tpl?.bcc || "");
    setBodyKey((key) => key + 1);
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPasswordTemplate = useCallback((tpl) => {
    form.setFieldValue("passwordTemplateId", tpl?._id || BUILTIN_TEMPLATE);
    form.setFieldValue(
      "passwordSubject",
      tpl?.subject || DEFAULT_AR_FORM_PASSWORD_SUBJECT
    );
    form.setFieldValue(
      "passwordBody",
      tpl?.body || DEFAULT_AR_FORM_PASSWORD_BODY
    );
    setPasswordBodyKey((key) => key + 1);
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await api.search({
        apiEntity: "emailTemplate",
        searchQuery: { type: { $in: ["AR_FORM", "AR_FORM_PASSWORD"] } },
        pageSize: 500,
        sort: { by: "name", order: "asc" },
        byAggregation: false,
      });
      const docs = uniqueTemplates(Array.isArray(res?.docs) ? res.docs : []);
      const documentTemplates = docs.filter((doc) => doc.type === "AR_FORM");
      const passwordDocs = docs.filter((doc) => doc.type === "AR_FORM_PASSWORD");
      setTemplates(documentTemplates);
      setPasswordTemplates(passwordDocs);
      return { documentTemplates, passwordDocs };
    } catch (error) {
      setTemplates([]);
      setPasswordTemplates([]);
      return { documentTemplates: [], passwordDocs: [] };
    } finally {
      setLoadingTemplates(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!opened) return undefined;
    setEntityFiles([]);
    setSelectedEntityFileIds([]);
    if (!transactionId) return undefined;
    let cancelled = false;
    form.setFieldValue("encrypt", false);
    form.clearErrors();
    setPreview(null);
    (async () => {
      const { documentTemplates, passwordDocs } = await loadTemplates();
      if (cancelled) return;
      const documentDefault =
        documentTemplates.find((doc) => doc.isDefault) || documentTemplates[0];
      const passwordDefault =
        passwordDocs.find((doc) => doc.isDefault) || passwordDocs[0];
      applyDocumentTemplate(documentDefault || null);
      applyPasswordTemplate(passwordDefault || null);
      try {
        const context = await api.OrderInvestmentTransaction.previewArFormEmail(
          transactionId,
          {
            arForm: transactionForm?.values?.arForm,
            subject: documentDefault?.subject || DEFAULT_AR_FORM_EMAIL_SUBJECT,
            body: documentDefault?.body || DEFAULT_AR_FORM_EMAIL_BODY,
            encrypt: false,
          }
        );
        if (cancelled) return;
        form.setFieldValue("to", context?.suggestedTo || "");
        setFilename(context?.filename || "");
        setEntityFiles(Array.isArray(context?.entityFiles) ? context.entityFiles : []);
        setEmailLog(Array.isArray(context?.emailLog) ? context.emailLog : []);
      } catch (error) {
        if (!cancelled) {
          setFilename("");
          setEntityFiles([]);
          setEmailLog([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, transactionId]);

  const insertPlaceholder = (markup) => {
    if (activeField === "subject" || activeField === "passwordSubject") {
      form.setFieldValue(activeField, `${form.values[activeField] || ""}${markup}`);
      return;
    }
    if (activeField === "passwordBody") {
      form.setFieldValue("passwordBody", `${form.values.passwordBody || ""}${markup}`);
      setPasswordBodyKey((key) => key + 1);
      return;
    }
    form.setFieldValue("body", `${form.values.body || ""}${markup}`);
    setBodyKey((key) => key + 1);
  };

  const payload = () => ({
    arForm: transactionForm?.values?.arForm,
    to: form.values.to,
    cc: form.values.cc,
    bcc: form.values.bcc,
    subject: form.values.subject,
    body: form.values.body,
    encrypt: form.values.encrypt,
    passwordSubject: form.values.passwordSubject,
    passwordBody: form.values.passwordBody,
    entityFileIds: selectedEntityFileIds,
  });

  const saveTemplate = async ({ name, type, subject, body, cc, bcc, apply }) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;
    setSavingTemplate(true);
    try {
      const res = await api.add({
        apiEntity: "emailTemplate",
        values: {
          name: trimmed,
          type,
          subject,
          body,
          cc: cc || "",
          bcc: bcc || "",
          isDefault: false,
        },
      });
      if (!res?.success) {
        const message =
          res?.error?.msg || res?.error?.message || res?.message || "";
        showNotification({
          color: "red",
          title: t("Could not save template"),
          message: typeof message === "string" ? message : "",
        });
        return;
      }
      showNotification({ color: "green", title: t("Template saved") });
      const loaded = await loadTemplates();
      const created = res.data;
      const list =
        type === "AR_FORM_PASSWORD"
          ? loaded.passwordDocs
          : loaded.documentTemplates;
      const match =
        (created?._id && list.find((doc) => doc._id === created._id)) ||
        list.find((doc) => doc.name === trimmed) ||
        created;
      apply(match || null);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePreview = async () => {
    if (!transactionId) return;
    setPreviewing(true);
    try {
      const data = await api.OrderInvestmentTransaction.previewArFormEmail(
        transactionId,
        payload()
      );
      setPreview(data);
      if (data?.filename) setFilename(data.filename);
      if (Array.isArray(data?.entityFiles)) {
        const files = data.entityFiles;
        setEntityFiles(files);
        setSelectedEntityFileIds((current) =>
          current.filter((id) => files.some((file) => file.id === id))
        );
      }
      if (Array.isArray(data?.emailLog)) setEmailLog(data.emailLog);
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Could not preview email"),
        message: error?.message || "",
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    const valid = form.validate();
    if (valid.hasErrors || !transactionId) return;
    setSending(true);
    const notifId = `ar-email-${transactionId}`;
    showNotification({
      id: notifId,
      loading: true,
      title: t("Sending AR Form…"),
      message: t("Generating the PDF and sending the email."),
      autoClose: false,
    });
    try {
      const data = await api.OrderInvestmentTransaction.sendArFormEmail(
        transactionId,
        payload()
      );
      if (Array.isArray(data?.emailLog)) setEmailLog(data.emailLog);
      updateNotification({
        id: notifId,
        loading: false,
        color: "green",
        title: t("AR Form sent"),
        message: data?.encrypted
          ? t("The PDF and a separate password email were sent.")
          : t("The AR Form PDF was emailed to the client."),
        autoClose: 4000,
      });
      onClose?.();
    } catch (error) {
      if (Array.isArray(error?.emailLog)) setEmailLog(error.emailLog);
      updateNotification({
        id: notifId,
        loading: false,
        color: "red",
        title: t("Could not send email"),
        message: error?.message || "",
        autoClose: 8000,
      });
    } finally {
      setSending(false);
    }
  };

  const templateOptions = (docs) => [
    { value: BUILTIN_TEMPLATE, label: t("Built-in default") },
    ...docs.map((tpl) => ({
      value: tpl._id,
      label: tpl.isDefault ? `${tpl.name} (${t("Default")})` : tpl.name,
    })),
  ];

  const recentLog = [...emailLog].reverse().slice(0, 8);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("Send AR Email to Client")}
      size="90%"
      styles={{ modal: { maxWidth: 900 } }}
      closeOnClickOutside={!sending}
    >
      <Stack spacing="sm">
        <Select
          label={t("Template")}
          data={templateOptions(templates)}
          value={form.values.templateId}
          onChange={(id) => {
            if (id === BUILTIN_TEMPLATE) {
              applyDocumentTemplate(null);
              return;
            }
            applyDocumentTemplate(templates.find((doc) => doc._id === id) || null);
          }}
          disabled={loadingTemplates || sending}
          searchable
        />
        <TextInput
          required
          label={t("To")}
          placeholder="name@company.com"
          {...form.getInputProps("to")}
          onFocus={() => setActiveField("to")}
          disabled={sending}
        />
        <TextInput
          label={t("CC")}
          placeholder={t("Optional")}
          {...form.getInputProps("cc")}
          onFocus={() => setActiveField("cc")}
          disabled={sending}
        />
        <TextInput
          label={t("BCC")}
          placeholder={t("Optional")}
          {...form.getInputProps("bcc")}
          onFocus={() => setActiveField("bcc")}
          disabled={sending}
        />
        <Group align="flex-end" noWrap spacing="sm">
          <TextInput
            required
            style={{ flex: 1 }}
            label={t("Subject")}
            {...form.getInputProps("subject")}
            onFocus={() => setActiveField("subject")}
            disabled={sending}
          />
          <EmailPlaceholderLegend
            tokens={
              activeField === "passwordSubject" || activeField === "passwordBody"
                ? AR_FORM_PASSWORD_PLACEHOLDERS
                : AR_FORM_EMAIL_PLACEHOLDERS
            }
            onInsert={insertPlaceholder}
            disabled={sending}
            mb={1}
          />
        </Group>
        <div onFocusCapture={() => setActiveField("body")}>
          <TextEditor
            key={bodyKey}
            form={form}
            name="body"
            label={t("Body")}
          />
          <Text size="xs" color="dimmed" mt={4}>
            {t("You can paste from Word. Tables, headings, lists, and colors are kept.")}
          </Text>
        </div>
        <Checkbox
          label={t("Password-protect the PDF")}
          checked={form.values.encrypt}
          onChange={(event) => {
            form.setFieldValue("encrypt", event.currentTarget.checked);
            setPreview(null);
          }}
          disabled={sending}
        />
        <Text size="xs" color="dimmed" mt={-8}>
          {t(
            "A second email with the password is sent to the same recipients. The password is created when you send and is not stored."
          )}{" "}
          {t("Password protection applies to PDFs only.")}
        </Text>
        {form.values.encrypt && (
          <Stack spacing="sm">
            <Select
              label={t("Password email template")}
              data={templateOptions(passwordTemplates)}
              value={form.values.passwordTemplateId}
              onChange={(id) => {
                if (id === BUILTIN_TEMPLATE) {
                  applyPasswordTemplate(null);
                  return;
                }
                applyPasswordTemplate(
                  passwordTemplates.find((doc) => doc._id === id) || null
                );
              }}
              disabled={loadingTemplates || sending}
              searchable
            />
            <TextInput
              label={t("Password email subject")}
              {...form.getInputProps("passwordSubject")}
              onFocus={() => setActiveField("passwordSubject")}
              disabled={sending}
            />
            <div onFocusCapture={() => setActiveField("passwordBody")}>
              <TextEditor
                key={passwordBodyKey}
                form={form}
                name="passwordBody"
                label={t("Password email body")}
              />
            </div>
          </Stack>
        )}
        <Alert icon={<IconPaperclip size={16} />} color="gray">
          <Text size="sm">
            {t("AR Form PDF will be attached")}:{" "}
            <Text span weight={600}>
              {filename || "AR_FORM.pdf"}
            </Text>
          </Text>
        </Alert>
        {transactionId ? (
          <EntityEmailStoredFiles
            files={entityFiles}
            selectedIds={selectedEntityFileIds}
            disabled={sending}
            onChange={(ids) => {
              setSelectedEntityFileIds(ids);
              setPreview(null);
            }}
          />
        ) : null}
        {preview && (
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm" weight={600} mb={4}>
              {t("Preview")}
            </Text>
            <Text size="sm" mb="xs">
              {preview.subject}
            </Text>
            <div dangerouslySetInnerHTML={{ __html: preview.body || "" }} />
            {form.values.encrypt && preview.passwordSubject ? (
              <>
                <Text size="sm" weight={600} mt="sm" mb={4}>
                  {t("Password email preview")}
                </Text>
                <Text size="sm" mb="xs">
                  {preview.passwordSubject}
                </Text>
                <div
                  dangerouslySetInnerHTML={{ __html: preview.passwordBody || "" }}
                />
              </>
            ) : null}
          </Paper>
        )}
        <Stack spacing={4}>
          <Text size="sm" weight={600}>
            {t("Email sent log")}
          </Text>
          {recentLog.length ? (
            recentLog.map((entry, index) => (
              <Text key={`${entry.sentAt || index}-${entry.to || ""}`} size="xs" color="dimmed">
                {formatSentAt(entry.sentAt)} — {entry.to}
                {entry.cc ? ` (cc ${entry.cc})` : ""}
                {entry.bcc ? ` (bcc ${entry.bcc})` : ""} — {entry.subject}
                {entry.encrypted
                  ? entry.passwordEmailSent
                    ? ` — ${t("password email sent")}`
                    : ` — ${t("password email failed")}`
                  : ""}
              </Text>
            ))
          ) : (
            <Text size="xs" color="dimmed">
              {t("No AR Form emails have been sent for this transaction.")}
            </Text>
          )}
        </Stack>
        <Group position="apart" mt="sm">
          <Group>
            <Popover
              width={280}
              position="top-start"
              withArrow
              shadow="md"
              opened={saveOpen}
              onChange={setSaveOpen}
            >
              <Popover.Target>
                <Button
                  variant="default"
                  leftIcon={<IconDeviceFloppy size={16} />}
                  disabled={sending}
                  onClick={() => setSaveOpen((open) => !open)}
                >
                  {t("Save as template")}
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack spacing="xs">
                  <TextInput
                    label={t("Template name")}
                    value={templateName}
                    onChange={(event) => setTemplateName(event.currentTarget.value)}
                    disabled={savingTemplate}
                  />
                  <Button
                    size="xs"
                    loading={savingTemplate}
                    onClick={async () => {
                      await saveTemplate({
                        name: templateName,
                        type: "AR_FORM",
                        subject: form.values.subject,
                        body: form.values.body,
                        cc: form.values.cc,
                        bcc: form.values.bcc,
                        apply: (tpl) => {
                          applyDocumentTemplate(tpl);
                          setSaveOpen(false);
                          setTemplateName("");
                        },
                      });
                    }}
                  >
                    {t("Save")}
                  </Button>
                </Stack>
              </Popover.Dropdown>
            </Popover>
            {form.values.encrypt && (
              <Popover
                width={280}
                position="top-start"
                withArrow
                shadow="md"
                opened={savePasswordOpen}
                onChange={setSavePasswordOpen}
              >
                <Popover.Target>
                  <Button
                    variant="default"
                    leftIcon={<IconDeviceFloppy size={16} />}
                    disabled={sending}
                    onClick={() => setSavePasswordOpen((open) => !open)}
                  >
                    {t("Save password template")}
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack spacing="xs">
                    <TextInput
                      label={t("Template name")}
                      value={passwordTemplateName}
                      onChange={(event) =>
                        setPasswordTemplateName(event.currentTarget.value)
                      }
                      disabled={savingTemplate}
                    />
                    <Button
                      size="xs"
                      loading={savingTemplate}
                      onClick={async () => {
                        await saveTemplate({
                          name: passwordTemplateName,
                          type: "AR_FORM_PASSWORD",
                          subject: form.values.passwordSubject,
                          body: form.values.passwordBody,
                          cc: form.values.cc,
                          bcc: form.values.bcc,
                          apply: (tpl) => {
                            applyPasswordTemplate(tpl);
                            setSavePasswordOpen(false);
                            setPasswordTemplateName("");
                          },
                        });
                      }}
                    >
                      {t("Save")}
                    </Button>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
            )}
          </Group>
          <Group>
            <Button variant="default" onClick={onClose} disabled={sending}>
              {t("Cancel")}
            </Button>
            <Button variant="default" onClick={handlePreview} loading={previewing} disabled={sending}>
              {t("Preview")}
            </Button>
            <Button onClick={handleSend} loading={sending}>
              {t("Send")}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
